import React, { useState, useEffect } from 'react';
import { VconLifecycleEvent, LedgerVerification, ApiResponse } from '../../types';
import { lifecycleService, LifecycleParams } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';

// The vCon lifecycle ledger (draft-howe-vcon-lifecycle): every event in the
// life of every vCon, hash-chained so tampering is detectable — the
// "Verify chain" button recomputes the whole chain server-side.

export const LIFECYCLE_EVENT_LABELS: Record<string, string> = {
  vcon_created: 'Created',
  vcon_enhanced: 'Enhanced',
  vcon_sent: 'Sent',
  vcon_received: 'Received',
  vcon_consent_accepted: 'Consent accepted',
  vcon_consent_revoked: 'Consent revoked',
  vcon_party_redacted: 'Party redacted',
  vcon_deleted: 'Deleted',
  vcon_expired: 'Expired',
  vcon_rcvr_purged: 'Receiver purged',
  ledger_compacted: 'Ledger compacted',
};

// Visual family per event: additions green-ish, transfers neutral,
// removals red-ish — reusing the existing status badge palette.
const EVENT_BADGE: Record<string, string> = {
  vcon_created: 'status-completed',
  vcon_received: 'status-completed',
  vcon_enhanced: 'status-active',
  vcon_sent: 'status-active',
  vcon_consent_accepted: 'status-completed',
  vcon_consent_revoked: 'status-terminated',
  vcon_party_redacted: 'status-terminated',
  vcon_deleted: 'status-failed',
  vcon_expired: 'status-failed',
  vcon_rcvr_purged: 'status-terminated',
  // Maintenance on the ledger itself rather than a vCon — deliberately the
  // attention-getting badge, since it marks a rewritten stretch of chain.
  ledger_compacted: 'status-failed',
};

export const lifecycleActorLabel = (e: VconLifecycleEvent): string => {
  const name = e.actor_name || '—';
  switch (e.actor_type) {
    case 'recorder': return 'Recorder';
    case 'console': return `Console: ${name}`;
    case 'mcp_app': return `MCP app: ${name}`;
    default: return `System: ${name}`;
  }
};

const LifecycleLedgerTable: React.FC = () => {
  const [entries, setEntries] = useState<VconLifecycleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<LifecycleParams>({ page: 1, limit: 25, search: '', event: '' });
  const [searchText, setSearchText] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [verification, setVerification] = useState<LedgerVerification | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    limit: 25,
    has_next: false,
    has_prev: false,
  });

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ApiResponse<VconLifecycleEvent> = await lifecycleService.list(searchParams);
      setEntries(response.entries || []);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch lifecycle ledger');
      console.error('Error fetching lifecycle ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchParams(prev => ({ ...prev, search: searchText.trim(), event: eventFilter, page: 1 }));
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerification(null);
    try {
      setVerification(await lifecycleService.verify());
    } catch (err) {
      console.error('Error verifying chain:', err);
      setError('Failed to verify the ledger chain');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return <div className="loading">Loading lifecycle ledger...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="settings-panel">
      <h3>vCon Lifecycle Ledger</h3>
      <p className="settings-hint">
        Append-only, hash-chained record of every vCon lifecycle event (per draft-howe-vcon-lifecycle):
        who created, received, enhanced, sent, or deleted which vCon, and when. Entries are never
        edited or removed — the ledger outlives the vCons it describes.
      </p>

      <form onSubmit={handleSearch} className="search-form">
        <input
          name="search"
          type="text"
          placeholder="Search by vCon UUID, filename, actor, IP, or detail..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <select value={eventFilter} onChange={e => setEventFilter(e.target.value)}>
          <option value="">All events</option>
          {Object.entries(LIFECYCLE_EVENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button type="submit">Search</button>
        <button
          type="button"
          onClick={() => {
            setSearchText('');
            setEventFilter('');
            setSearchParams(prev => ({ ...prev, search: '', event: '', page: 1 }));
          }}
        >
          Clear
        </button>
        <button type="button" disabled={verifying} onClick={handleVerify} title="Recompute every hash in the chain">
          {verifying ? 'Verifying…' : 'Verify chain'}
        </button>
        {verification && (
          <span className={`status ${verification.ok ? 'status-completed' : 'status-failed'}`}>
            {verification.ok
              ? `✓ chain intact (${verification.entries} entries)`
              : `✗ TAMPERED — first bad entry #${verification.first_bad_id ?? '?'}${verification.head_ok ? '' : ', head mismatch'}`}
          </span>
        )}
      </form>

      <div className="pagination-info">
        Showing {entries.length} of {pagination.total_count} ledger entries (newest first)
      </div>

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Time</th>
              <th>Event</th>
              <th>vCon</th>
              <th>Actor</th>
              <th>IP</th>
              <th>Detail</th>
              <th>Entry hash</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={8} className="no-data">No lifecycle events recorded</td></tr>
            ) : (
              entries.map(entry => (
                <tr key={entry.id}>
                  <td>{entry.id}</td>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>
                    <span className={`status ${EVENT_BADGE[entry.event] || 'status-active'}`}>
                      {LIFECYCLE_EVENT_LABELS[entry.event] || entry.event}
                    </span>
                  </td>
                  <td className="monospace small">
                    <div>{entry.vcon_uuid}</div>
                    {entry.vcon_filename && <div>{entry.vcon_filename}</div>}
                  </td>
                  <td className="small">{lifecycleActorLabel(entry)}</td>
                  <td className="monospace small">{entry.source_ip || '—'}</td>
                  <td className="monospace small" title={entry.detail || undefined}>
                    {entry.detail
                      ? (entry.detail.length > 60 ? entry.detail.slice(0, 60) + '…' : entry.detail)
                      : '—'}
                  </td>
                  <td className="monospace small" title={entry.entry_hash}>{entry.entry_hash.slice(0, 12)}…</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button disabled={!pagination.has_prev} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page - 1 }))}>Previous</button>
          <span className="page-info">Page {pagination.current_page} of {pagination.total_pages}</span>
          <button disabled={!pagination.has_next} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page + 1 }))}>Next</button>
        </div>
      )}
    </div>
  );
};

export default LifecycleLedgerTable;
